import React, { useRef, useEffect, useMemo } from "react";
import Editor from "@monaco-editor/react";
import { registerCompletions } from "../monaco/completions.js";
import { registerHovers } from "../monaco/hovers.js";
import { validateRule } from "../core/validator.js";
import { collectReferences } from "../core/references.js";
import { registerRuleLanguageMonaco } from '../monaco/registerLanguage';

export function RuleEditor({
    value = "",
    onChange,
    variables = [],
    varDocs = new Map(),
    varTypes = new Map(),
    height = "340px",
    readOnly = false,
    className,
}) {
    const monacoRef = useRef(null);
    const modelRef = useRef(null);
    const disposablesRef = useRef([]);
    const langIdRef = useRef(null);
    const editorRef = useRef(null);

    const initial = useMemo(() => value, []);

    const handleValidateAndReport = (text) => {
        if (text === undefined || text === null) text = "";
        const { ast, errors } = validateRule(text);
        const references = collectReferences(text);

        console.log('ast', ast);

        if (monacoRef.current && modelRef.current) {
            const monaco = monacoRef.current;
            const markers = errors.map(e => ({
                startLineNumber: e.pos.line,
                startColumn: e.pos.col,
                endLineNumber: e.pos.line,
                endColumn: e.pos.col + 1,
                message: e.message,
                severity: monaco.MarkerSeverity.Error,
                source: "rulelang",
            }));
            monaco.editor.setModelMarkers(modelRef.current, "rulelang", markers);
        }

        onChange && onChange(text, {
            errors: errors.map(e => ({ message: e.message, line: e.pos.line, col: e.pos.col })),
            references
        });
    };

    const beforeMount = (monaco) => {
        monacoRef.current = monaco;
        const langId = registerRuleLanguageMonaco(monaco);
        langIdRef.current = langId;
    };

    // Helper: (re)install providers using latest props
    const installProviders = () => {
        const monaco = monacoRef.current;
        const langId = langIdRef.current;
        if (!monaco || !langId) return;

        // Dispose previous providers (hot reload / prop changes)
        disposablesRef.current.forEach(d => d?.dispose?.());
        disposablesRef.current = [];

        disposablesRef.current.push(registerCompletions(monaco, langId, { variables, varDocs, varTypes }));
        disposablesRef.current.push(registerHovers(monaco, langId, { varDocs, varTypes }));

        // eslint-disable-next-line no-console
        console.log("[RuleEditor] Providers (re)installed", {
            langId,
            variablesCount: variables.length,
            docs: varDocs.size,
            types: varTypes.size,
        });
    };

    const onMount = (editor, monaco) => {
        editorRef.current = editor;
        const model = editor.getModel();
        if (!model) return;
        modelRef.current = model;

        editor.updateOptions({
            suggestOnTriggerCharacters: true,
            quickSuggestions: { other: true, comments: false, strings: true },
            acceptSuggestionOnEnter: "smart",
            inlineSuggest: { enabled: true },
            parameterHints: { enabled: true },
            wordBasedSuggestions: true,
        });

        // Auto-trigger suggest on key chars
        const typed = editor.onDidType((text) => {
            if (/[${%<|&!`,[(]/.test(text)) {
                editor.trigger("rulelang", "editor.action.triggerSuggest", {});
            }
        });
        disposablesRef.current.push({ dispose: () => typed.dispose() });

        // Initial install with current props
        installProviders();

        // Initial validation
        handleValidateAndReport(model.getValue());
    };

    // // Re-install providers whenever variables/docs/types change
    // useEffect(() => {
    //     installProviders();
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [variables, varDocs, varTypes]);

    useEffect(() => {
        return () => {
            disposablesRef.current.forEach(d => d?.dispose?.());
            disposablesRef.current = [];
        };
    }, []);

    return (
        <Editor
            className={className}
            beforeMount={beforeMount}
            onMount={onMount}
            defaultLanguage="rulelang"
            defaultValue={initial}
            onChange={(text) => { if (typeof text === "string") handleValidateAndReport(text); }}
            height={height}
            theme="vs-dark"
            options={{
                readOnly,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                wordWrap: "on",
                fontLigatures: true,
                tabSize: 2,
                insertSpaces: true,
                automaticLayout: true,
                renderWhitespace: "selection",
            }}
        />
    );
}
